/**
 * ScaliqCampaignsPanel — ScalIQ LinkedIn-campagnes op de Campagnes-pagina.
 *
 * Read-only overzicht (de ScalIQ-API kan campagnes niet aanmaken/pauzeren):
 * campagne-kaarten met stats → detail-drawer met de lead-lijst (status,
 * replied, tag) + "meer laden" via cursor-paginatie.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    Rocket, Loader2, X, ChevronDown, ChevronRight, Users,
    ExternalLink, MessageCircle, CheckCircle2,
} from "lucide-react";

const STATUS_META = {
    CAMPAIGN_IN_PROGRESS: { label: "Actief", cls: "text-[#22c55e] border-[#22c55e]/40" },
    CAMPAIGN_PAUSED: { label: "Gepauzeerd", cls: "text-[var(--jr-amber)] border-[var(--jr-amber)]/40" },
    READY_TO_SEND: { label: "Klaar", cls: "text-[var(--jr-info)] border-[var(--jr-info)]/40" },
};

export default function ScaliqCampaignsPanel() {
    const [open, setOpen] = useState(true);
    const [campaigns, setCampaigns] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notConfigured, setNotConfigured] = useState(false);
    const [detail, setDetail] = useState(null); // campaign id in drawer

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await api.get("/scaliq/campaigns");
                setCampaigns(r.data.campaigns || []);
            } catch (e) {
                if (e?.response?.status === 400) setNotConfigured(true);
                setCampaigns([]);
            } finally { setLoading(false); }
        })();
    }, []);

    if (notConfigured) return null; // geen key → sectie verbergen

    return (
        <section className="mt-8" data-testid="scaliq-campaigns-section">
            <button onClick={() => setOpen((o) => !o)} data-testid="scaliq-campaigns-toggle"
                className="flex items-center gap-2 text-sm font-medium text-white hover:text-[#818cf8]">
                {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <Rocket size={15} className="text-[#818cf8]" />
                {i18n.t("scaliq.campaigns_section")} {campaigns ? `(${campaigns.length})` : ""}
            </button>
            <p className="mt-1 ml-6 text-[11px] text-[var(--jr-text-3)]">{i18n.t("scaliq.campaigns_section_note")}</p>
            {open && (
                <div className="mt-3">
                    {loading ? (
                        <div className="text-[var(--jr-text-2)] text-sm flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" /> {i18n.t("common.loading")}
                        </div>
                    ) : (
                        <div className="grid lg:grid-cols-2 gap-3">
                            {(campaigns || []).map((c) => {
                                const st = STATUS_META[c.status] || { label: c.status, cls: "text-[var(--jr-text-3)] border-white/15" };
                                const stats = c.stats || {};
                                return (
                                    <button key={c.id} onClick={() => setDetail(c)}
                                        data-testid={`scaliq-campaign-card-${c.id}`}
                                        className="text-left rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4 hover:border-[#6366f1]/50 transition-colors">
                                        <div className="flex items-center gap-2">
                                            <span className={`shrink-0 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                                            <span className="text-sm text-white truncate font-medium flex-1" title={c.name}>{c.name}</span>
                                        </div>
                                        <div className="mt-2.5 grid grid-cols-4 gap-2 text-center">
                                            {[
                                                [c.totalLeads, "Leads"],
                                                [`${stats.acceptanceRate ?? "–"}%`, "Accept"],
                                                [stats.messagesSent ?? 0, "DM's"],
                                                [`${stats.replyRate ?? "–"}%`, "Reply"],
                                            ].map(([v, l]) => (
                                                <div key={l}>
                                                    <div className="text-sm font-semibold text-white">{v}</div>
                                                    <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">{l}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {detail && <CampaignDrawer campaign={detail} onClose={() => setDetail(null)} />}
        </section>
    );
}

function CampaignDrawer({ campaign, onClose }) {
    const [leads, setLeads] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = async (cur) => {
        try {
            const r = await api.get(`/scaliq/campaigns/${campaign.id}`, { params: cur ? { cursor: cur } : {} });
            setLeads((prev) => cur ? [...prev, ...(r.data.leads || [])] : (r.data.leads || []));
            setCursor(r.data.next_cursor || null);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("scaliq.campaigns_failed")); }
    };
    useEffect(() => {
        (async () => { setLoading(true); await load(null); setLoading(false); })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.id]);

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="scaliq-campaign-drawer">
            <div className="absolute inset-0 bg-black/55" onClick={onClose} />
            <div className="relative w-full max-w-xl h-full bg-[var(--jr-surface)] border-l border-white/10 flex flex-col">
                <header className="p-5 border-b border-white/10">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-[#818cf8]">
                                <Rocket size={11} /> ScalIQ campagne
                            </div>
                            <h3 className="mt-1 text-base font-medium text-white leading-snug">{campaign.name}</h3>
                        </div>
                        <button onClick={onClose} data-testid="scaliq-drawer-close"
                            className="p-1.5 rounded-full text-[var(--jr-text-3)] hover:text-white hover:bg-white/10"><X size={16} /></button>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-[11px] text-[var(--jr-text-2)]">
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {campaign.totalLeads} leads</span>
                        <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} /> {(campaign.stats || {}).acceptanceRate ?? "–"}% accept</span>
                        <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {(campaign.stats || {}).replyRate ?? "–"}% reply</span>
                        <a href="https://app.scaliq.ai" target="_blank" rel="noreferrer"
                            className="ml-auto inline-flex items-center gap-1 text-[#818cf8] hover:underline">
                            {i18n.t("scaliq.manage_in_scaliq")} <ExternalLink size={10} />
                        </a>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-4 space-y-1.5" data-testid="scaliq-drawer-leads">
                    {loading ? (
                        <div className="grid place-items-center py-10"><Loader2 size={18} className="animate-spin text-[var(--jr-text-3)]" /></div>
                    ) : leads.length === 0 ? (
                        <p className="text-sm text-[var(--jr-text-3)] text-center py-8">{i18n.t("scaliq.no_leads_in_campaign")}</p>
                    ) : leads.map((l) => (
                        <a key={l.id} href={l.linkedinUrl || undefined} target="_blank" rel="noreferrer"
                            className="flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2 hover:border-[#6366f1]/40">
                            <div className="min-w-0 flex-1">
                                <div className="text-xs text-white truncate">{l.fullName || "—"}</div>
                                <div className="text-[10px] text-[var(--jr-text-3)] truncate">
                                    {[l.jobTitle, l.companyName || null].filter(Boolean).join(" · ") || l.location || ""}
                                </div>
                            </div>
                            {l.replied && <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full border border-[var(--jr-brand)]/50 text-[var(--jr-brand)]">reply</span>}
                            {l.tag && <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full border border-[var(--jr-amber)]/40 text-[var(--jr-amber)]">{l.tag}</span>}
                            <span className="shrink-0 text-[9px] font-mono uppercase text-[var(--jr-text-3)]">{l.stage || ""}</span>
                        </a>
                    ))}
                    {cursor && !loading && (
                        <button onClick={async () => { setLoadingMore(true); await load(cursor); setLoadingMore(false); }}
                            disabled={loadingMore} data-testid="scaliq-drawer-more"
                            className="w-full mt-2 py-2 rounded-lg border border-white/10 text-xs text-[var(--jr-text-2)] hover:text-white hover:border-[#6366f1]/40 disabled:opacity-50">
                            {loadingMore ? <Loader2 size={12} className="animate-spin inline" /> : i18n.t("scaliq.load_more")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
